"use client";

import React, { useRef, useState } from "react";
import type { UserInfo } from "../types";
import { cn, findMentionQuery, insertMention, matchMentionCandidates } from "../utils";

interface CommentInputProps {
    value: string;
    onChange: (value: string) => void;
    onSubmit: () => void;
    /** Who can be tagged. */
    members: UserInfo[];
    disabled?: boolean;
}

/**
 * The comment box. Typing `@` opens a list of the team to tag; ↑/↓ move,
 * Enter or Tab picks, Escape closes. Enter otherwise sends.
 */
export const CommentInput: React.FC<CommentInputProps> = ({
    value,
    onChange,
    onSubmit,
    members,
    disabled,
}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [mention, setMention] = useState<{ start: number; query: string } | null>(null);
    const [active, setActive] = useState(0);

    const candidates = mention ? matchMentionCandidates(members, mention.query) : [];
    const open = candidates.length > 0;

    const track = (text: string, caret: number | null) => {
        const next = caret === null ? null : findMentionQuery(text, caret);
        if (next?.query !== mention?.query || next?.start !== mention?.start) setActive(0);
        setMention(next);
    };

    const pick = (member: UserInfo) => {
        const input = inputRef.current;
        if (!mention || !input) return;
        const caret = input.selectionStart ?? value.length;
        const next = insertMention(value, mention.start, caret, member.name);
        onChange(next.text);
        setMention(null);
        // After React has written the new value.
        requestAnimationFrame(() => {
            input.focus();
            input.setSelectionRange(next.caret, next.caret);
        });
    };

    return (
        <div className="relative flex-1">
            {open && (
                <ul
                    role="listbox"
                    aria-label="Tag someone"
                    className="absolute bottom-full left-0 right-0 mb-1 p-1 m-0 list-none bg-white border border-gray-200 rounded-xl shadow-lg z-10 max-h-56 overflow-y-auto"
                >
                    {candidates.map((member, i) => (
                        <li key={member.id} role="option" aria-selected={i === active}>
                            <button
                                type="button"
                                // mousedown, so the input keeps focus and its caret.
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    pick(member);
                                }}
                                onMouseEnter={() => setActive(i)}
                                className={cn(
                                    "w-full flex flex-col items-start px-3 py-1.5 rounded-lg text-left",
                                    i === active ? "bg-matter-ink/10" : "hover:bg-matter-ink/5",
                                )}
                            >
                                <span className="text-sm! font-medium text-gray-900">{member.name}</span>
                                <span className="text-[10px] text-gray-500">{member.email}</span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            <input
                ref={inputRef}
                type="text"
                value={value}
                disabled={disabled}
                onChange={(e) => {
                    onChange(e.target.value);
                    track(e.target.value, e.target.selectionStart);
                }}
                onSelect={(e) => track(e.currentTarget.value, e.currentTarget.selectionStart)}
                onBlur={() => setMention(null)}
                placeholder="Add a comment… type @ to tag someone"
                aria-autocomplete="list"
                aria-expanded={open}
                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm! focus:outline-none focus:bg-white focus:border-matter-ink/40 focus:shadow-lg focus:shadow-matter-ink/10 transition-all"
                onKeyDown={(e) => {
                    if (open) {
                        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                            e.preventDefault();
                            const step = e.key === "ArrowDown" ? 1 : -1;
                            setActive((i) => (i + step + candidates.length) % candidates.length);
                            return;
                        }
                        if (e.key === "Enter" || e.key === "Tab") {
                            e.preventDefault();
                            pick(candidates[active] ?? candidates[0]);
                            return;
                        }
                        if (e.key === "Escape") {
                            // Closes the list, not the widget.
                            e.preventDefault();
                            e.stopPropagation();
                            setMention(null);
                            return;
                        }
                    }
                    if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        onSubmit();
                    }
                }}
            />
        </div>
    );
};
